



































































































































































































































































import { AxiosError } from 'axios';
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, truncate } from 'lodash';
import moment from 'moment';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';

import {
  IAttachment,
  IVacationCarryOverRequest,
  IMentionAttachmentComment,
  VacationCarryOverRequestComment,
  VacationCarryOverRequestCommentAttachment,
  VacationCarryOverRequestCommentFieldError
} from '@/models';
import { ErrorManager, ISystemError } from '@/models/error';

import {
  createFile,
  diffDeep,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';

import CommentAttachmentList from '../common/CommentAttachmentList.vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import QuillEditor from '../common/QuillEditor.vue';
import MentionProvider from '../mention/MentionProvider.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';

export default mixins(auth, snackbar).extend({
  name: 'VacationCarryOverRequestCommentListItem',

  components: {
    CommentAttachmentList,
    ConfirmationDialog,
    DeleteConfirmationDialog,
    QuillEditor,
    MentionProvider
  },

  props: {
    vacationCarryOverRequest: {
      type: Object as PropType<IVacationCarryOverRequest>,
      required: true
    },

    vacationCarryOverRequestComment: {
      type: Object as PropType<IMentionAttachmentComment>,
      required: true
    }
  },

  data() {
    return {
      isEditing: false,

      commentLoading: false,

      commentErrorMessage: '',
      commentErrorMessageDetail: '',

      // delete comment
      deleteCommentError: null as ISystemError | null,
      deleteCommentErrorMessageDetail: '',
      commentDeleteInProgress: false,
      selectedVacationCarryOverRequestComment: cloneDeep(
        this.vacationCarryOverRequestComment
      ),

      deleteVacationCarryOverRequestCommentDialog: false,

      commentTextHtml: this.vacationCarryOverRequestComment.getTextHtml(),
      mentionsLoading: false,

      deleteCommentAttachmentDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IAttachment,
      attachments: [] as IAttachment[]
    };
  },

  computed: {
    ...mapGetters({
      mentionableUsers:
        'vacationCarryOverRequest/getVacationCarryOverRequestMentionableUsers'
    }),

    isVacationCarryOverRequestCommentOwner(): boolean {
      return (
        this.getCurrentUserFullName ===
        this.vacationCarryOverRequestComment.owner
      );
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedVacationCarryOverRequestComment.text) &&
        !this.commentLoading
      );
    },

    textHasDifference(): boolean {
      return (
        this.selectedVacationCarryOverRequestComment.text ===
        this.vacationCarryOverRequestComment.text
      );
    }
  },

  methods: {
    ...mapActions({
      updateVacationCarryOverRequestComment:
        'vacationCarryOverRequest/updateVacationCarryOverRequestComment',
      deleteVacationCarryOverRequestComment:
        'vacationCarryOverRequest/deleteVacationCarryOverRequestComment',
      addVacationCarryOverRequestCommentAttachments:
        'vacationCarryOverRequest/addVacationCarryOverRequestCommentAttachments',
      removeVacationCarryOverRequestCommentAttachment:
        'vacationCarryOverRequest/removeVacationCarryOverRequestCommentAttachment',
      fetchVacationCarryOverRequestCommentsMentionableUsers:
        'vacationCarryOverRequest/fetchVacationCarryOverRequestCommentsMentionableUsers'
    }),

    truncateFileName(filename: string) {
      const extension = filename.split('.').pop()!;

      return `${truncate(filename, {
        length: 30,
        omission: '[...]'
      })}${extension}`;
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new VacationCarryOverRequestCommentAttachment({ file: file })
        )
      );
    },

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    toggleEditingComment() {
      if (!this.isEditing) {
        // To reset the selected vacationCarryOverRequest comment data to the original when rediting
        this.selectedVacationCarryOverRequestComment = cloneDeep(
          this.vacationCarryOverRequestComment
        );
        this.commentTextHtml = this.vacationCarryOverRequestComment.getTextHtml();
      }
      this.isEditing = !this.isEditing;
    },

    stopEditingComment() {
      if (
        !this.selectedVacationCarryOverRequestComment.text ||
        this.textHasDifference
      ) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.deleteCommentErrorMessageDetail = '';
      this.commentErrorMessage = '';
      this.commentErrorMessageDetail = '';
      this.deleteCommentError = null;
      this.attachmentDeleteError = null;
    },

    closeCommentDeleteDialog() {
      this.clearAllErrors();
      this.deleteVacationCarryOverRequestCommentDialog = false;
    },

    removeVacationCarryOverRequestComment(comment: IMentionAttachmentComment) {
      this.clearAllErrors();
      this.commentDeleteInProgress = true;
      this.deleteVacationCarryOverRequestComment({
        overtimeId: this.vacationCarryOverRequest.id,
        commentId: comment.id
      })
        .then(() => {
          this.closeCommentDeleteDialog();
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.deleteCommentErrorMessageDetail = error.response.data.detail;
            }
          }
          this.deleteCommentError = setErrorFromAxiosError(error as AxiosError);
        })
        .finally(() => {
          this.commentDeleteInProgress = false;
        });
    },

    editComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = VacationCarryOverRequestComment.parseHtmlToMentionText(
        this.commentTextHtml
      );

      this.selectedVacationCarryOverRequestComment.text = parsedCommentMentionText;

      const commentDelta = diffDeep(
        this.selectedVacationCarryOverRequestComment,
        this.vacationCarryOverRequestComment,
        true
      );

      return this.updateVacationCarryOverRequestComment({
        overtimeId: this.vacationCarryOverRequest.id,
        comment: commentDelta
      })
        .then(async () => {
          if (this.attachments.length !== 0) {
            await this.addVacationCarryOverRequestCommentAttachments({
              overtimeId: this.vacationCarryOverRequest.id,
              commentId: commentDelta.id,
              attachments: this.attachments
            });
            this.attachments = [];
          }
          this.selectedVacationCarryOverRequestComment = cloneDeep(
            this.vacationCarryOverRequestComment
          );
          this.isEditing = false;
        })
        .catch(error => {
          if (error.response) {
            // client received an error response that falls out of range 2xx
            if (error.response.data.detail) {
              this.commentErrorMessageDetail = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchVacationCarryOverRequestCommentsMentionableUsers(
          this.vacationCarryOverRequestComment.id
        );
      } catch (error) {
        this.commentErrorMessage = setErrorFromAxiosError(
          error as AxiosError
        ).message;
      } finally {
        this.mentionsLoading = false;
      }
    },

    downloadAttachmentFile(attachment: IAttachment) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    },

    openAttachmentDeleteDialog(attachment: IAttachment) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.deleteCommentAttachmentDialog = true;
    },

    cancelCommentAttachmentDeleteDialog() {
      this.deleteCommentAttachmentDialog = false;
    },

    removeCommentAttachment(attachment: IAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removeVacationCarryOverRequestCommentAttachment({
        overtimeId: this.vacationCarryOverRequest.id,
        commentId: this.vacationCarryOverRequestComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.cancelCommentAttachmentDeleteDialog();
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    }
  }
});
